import React, { useState, useEffect } from 'react';
import { Badge, Dropdown, List, Button, Typography, Empty, Spin } from 'antd';
import { BellOutlined, CheckOutlined } from '@ant-design/icons';
import apiService from 'services/api/apiService';
import './NotificationBell.css';

const { Text } = Typography;
const API = '/backend/hr-api/notifications/notifications.php';

interface Notification {
  id: string;
  notification_id: string;
  type: string;
  title: string;
  message: string;
  status: string;
  created_at: string;
}

const NotificationBell: React.FC = () => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading]             = useState(false);
  const [open, setOpen]                   = useState(false);
  const [unreadCount, setUnreadCount]     = useState(0);
  const tenantId = apiService.getTenantId();

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const res  = await fetch(`${API}?tenant_id=${tenantId}&action=list`);
      const data = await res.json();
      if (data.success) {
        setNotifications(data.data || []);
        setUnreadCount(data.unread_count ?? 0);
      }
    } catch (e) {
      console.error('Failed to fetch notifications:', e);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (notificationId: string) => {
    try {
      await fetch(API, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'mark_read', notification_id: notificationId, tenant_id: tenantId })
      });
      setNotifications(prev => prev.map(n =>
        n.notification_id === notificationId ? { ...n, status: 'read' } : n
      ));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (e) { console.error('Mark read error:', e); }
  };

  const markAllRead = async () => {
    try {
      await fetch(API, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'mark_all_read', tenant_id: tenantId })
      });
      setNotifications(prev => prev.map(n => ({ ...n, status: 'read' })));
      setUnreadCount(0);
    } catch (e) { console.error('Mark all read error:', e); }
  };

  const getIcon = (type: string) => {
    const icons: Record<string, string> = {
      leave_approved: '✅', leave_rejected: '❌',
      leave_submitted: '📝', performance_review: '📊',
      training_assigned: '📚', general: '🔔'
    };
    return icons[type] ?? '🔔';
  };

  const timeAgo = (dateString: string) => {
    const diff = Math.floor((Date.now() - new Date(dateString).getTime()) / 60000);
    if (diff < 1)    return 'Just now';
    if (diff < 60)   return `${diff}m ago`;
    if (diff < 1440) return `${Math.floor(diff / 60)}h ago`;
    return `${Math.floor(diff / 1440)}d ago`;
  };

  const dropdownContent = (
    <div className="notification-dropdown" style={{ width: 360 }}>
      <div className="notification-header" style={{
        display: 'flex', justifyContent: 'space-between',
        alignItems: 'center', padding: '12px 16px',
        borderBottom: '1px solid #f0f0f0'
      }}>
        <Text strong>Notifications {unreadCount > 0 && `(${unreadCount} unread)`}</Text>
        {unreadCount > 0 && (
          <Button size="small" type="link" icon={<CheckOutlined />} onClick={markAllRead}>
            Mark all read
          </Button>
        )}
      </div>

      <div style={{ maxHeight: 380, overflowY: 'auto' }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: 20 }}><Spin /></div>
        ) : notifications.length === 0 ? (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="No notifications" style={{ padding: 20 }} />
        ) : (
          <List
            dataSource={notifications.slice(0, 10)}
            renderItem={(item) => (
              <List.Item
                style={{
                  padding: '10px 16px', cursor: item.status === 'unread' ? 'pointer' : 'default',
                  background: item.status === 'unread' ? '#e6f7ff' : 'transparent'
                }}
                onClick={() => item.status === 'unread' && markAsRead(item.notification_id)}
              >
                <div style={{ display: 'flex', gap: 10, width: '100%' }}>
                  <span style={{ fontSize: 20 }}>{getIcon(item.type)}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <Text strong={item.status === 'unread'} style={{ display: 'block' }}>
                      {item.title}
                    </Text>
                    <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
                      {item.message?.substring(0, 80)}{item.message?.length > 80 ? '...' : ''}
                    </Text>
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      {timeAgo(item.created_at)}
                    </Text>
                  </div>
                  {item.status === 'unread' && (
                    <div style={{
                      width: 8, height: 8, borderRadius: '50%',
                      background: '#1890ff', marginTop: 6, flexShrink: 0
                    }} />
                  )}
                </div>
              </List.Item>
            )}
          />
        )}
      </div>
    </div>
  );

  return (
    <Dropdown
      dropdownRender={() => dropdownContent}
      trigger={['click']}
      placement="bottomRight"
      open={open}
      onOpenChange={setOpen}
    >
      <Badge count={unreadCount} size="small">
        <Button
          type="text"
          icon={<BellOutlined style={{ fontSize: 18 }} />}
          style={{ display: 'flex', alignItems: 'center',
                   justifyContent: 'center', height: 40, width: 40 }}
        />
      </Badge>
    </Dropdown>
  );
};

export default NotificationBell;
